// api.js
import axios from 'axios';
import { cacheKeys } from "@/utils/cache";

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL || 'http://localhost:1337',
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
});

// api.js
export const fetchProjects = async () => {
  try {
    // Try to get from cache
    const cached = localStorage.getItem(cacheKeys.PROJECTS);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      const isValid = Date.now() - timestamp < 24 * 60 * 60 * 1000;
      if (isValid) return data;
    }

    // No cache or expired - fetch fresh
    const response = await apiClient.get('/api/projects?populate[images][populate]=*&pagination[pageSize]=100');

    if (response.status === 200) {
      // Try to cache, but don't break if it fails
      try {
        localStorage.setItem(cacheKeys.PROJECTS, JSON.stringify({
          data: response.data.data,
          timestamp: Date.now()
        }));
      } catch (e) {
        // Cache failed, no big deal - continue with fresh data
      }
      return response.data.data;
    }
  } catch (error) {
    console.error('Error fetching projects:', error);
    throw error;
  }
};

export const fetchInfoLinks = async () => {
  try {
    // Fetch fresh data - no caching
    const response = await apiClient.get('/api/info-links');


    if (response.status === 200) {
      // Transform the data to match the expected format
      const transformedData = {
        // Process personal exhibitions to remove URLs
        personalExhibitions: (response.data.data.find(item => item.id === 2)?.attributes.text || [])
            .map(text => {
              // Remove URLs from exhibition entries
              return cleanExhibitionText(text);
            }),

        // Process group exhibitions to remove URLs
        groupExhibitions: (response.data.data.find(item => item.id === 1)?.attributes.text || [])
            .map(text => {
              // Remove URLs from exhibition entries
              return cleanExhibitionText(text);
            }),

        // Process publications to extract URLs for linking
        selectedPublications: (response.data.data.find(item => item.id === 3)?.attributes.text || [])
            .map(pub => processPublicationText(pub))
      };

      return transformedData;
    }

    throw new Error('Failed to fetch info links');
  } catch (e) {
    console.error('Error fetching info links:', e);
    // Return empty data structure instead of throwing
    return {
      personalExhibitions: [],
      groupExhibitions: [],
      selectedPublications: []
    };
  }
};

// Helper function to clean exhibition text and remove URLs
function cleanExhibitionText(text) {
  // Special case for "URL : ."
  if (text.includes("URL : .")) {
    text = text.replace("URL : .", "");
  }

  // First, clean URLs with "URL:" prefix (various formats)
  let cleaned = text.replace(/\s+URL\.?:?\s*(?:https?:\/\/)?[^\s]*/ig, '');

  // Then clean any remaining URLs without "URL:" prefix
  cleaned = cleaned.replace(/\s+(https?:\/\/[^\s]+)/ig, '');

  // Clean domain-style URLs (www.example.com or example.com/path)
  cleaned = cleaned.replace(/\s+(www\.[^\s]+|[^\s\.]+\.[a-z]{2,}\/[^\s]*)/ig, '');

  // Special case for izolyatsia.org
  cleaned = cleaned.replace(/\s+izolyatsia\.org[^\s]*/ig, '');

  // Clean up any double spaces that might have been created
  cleaned = cleaned.replace(/\s{2,}/g, ' ').trim();

  return cleaned;
}

// Helper function to process publication text and extract URL
function processPublicationText(text) {
  // First clean the text of URLs and get the cleaned version
  const cleanedText = cleanExhibitionText(text);

  // Then extract the URL if present in the original text

  // Check for URL patterns
  const urlPattern = /URL:?\s*(https?:\/\/[^\s]+|www\.[^\s]+|[^\s\.]+\.[a-z]{2,}\/[^\s]*)/i;
  const urlMatch = text.match(urlPattern);

  if (urlMatch) {
    let url = urlMatch[1];
    // Add http:// if it doesn't have a protocol
    if (!url.startsWith('http')) {
      url = 'http://' + url;
    }

    return {
      title: cleanedText,
      url: url
    };
  }

  // Check for http/https URLs without URL: prefix
  const httpPattern = /(https?:\/\/[^\s]+)/i;
  const httpMatch = text.match(httpPattern);

  if (httpMatch) {
    return {
      title: cleanedText,
      url: httpMatch[1]
    };
  }

  // Check for domain URLs without http/https
  const domainPattern = /\s+(www\.[^\s]+|[^\s\.]+\.[a-z]{2,}\/[^\s]*)/i;
  const domainMatch = text.match(domainPattern);

  if (domainMatch) {
    let url = domainMatch[1];
    if (!url.startsWith('http')) {
      url = 'http://' + url;
    }

    return {
      title: cleanedText,
      url: url
    };
  }

  // No URL found, return cleaned text with no URL
  return {
    title: cleanedText,
    url: '#'
  };
}

// Legacy helper function (keeping for backward compatibility)
function extractUrl(text) {
  const urlMatch = text.match(/URL:\s*(https?:\/\/[^\s]+)/);
  return urlMatch ? urlMatch[1] : '#';
}

export const getFullUrl = (path) => {
  if (!path) return '';
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${apiClient.defaults.baseURL}/${cleanPath}`;
};

export const sortProjectsByDate = (projects) => {
  const getYear = (date) => {
    if (!date) return 0;
    const years = date.split('-').map((year) => parseInt(year, 10));
    return Math.min(...years);
  };

  return [...projects].sort((a, b) => getYear(a.attributes.date) - getYear(b.attributes.date));
};

export default apiClient;